import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Account } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Database, MessageSquare, HelpCircle, FileText, AlertTriangle, Car, LayoutTemplate } from 'lucide-react'

type DataType = 'vehicles' | 'reviews' | 'faqs' | 'pages' | 'page_content'

interface LoadSampleDataDialogProps {
  account: Account | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface LoadResult {
  type: string
  success: boolean
  count: number
  message: string
}

interface LoadResponse {
  success: boolean
  account: { id: string; name: string; slug: string }
  results: LoadResult[]
  total_count: number
  message: string
}

export function LoadSampleDataDialog({ account, open, onOpenChange }: LoadSampleDataDialogProps) {
  const { toast } = useToast()
  const [selectedTypes, setSelectedTypes] = useState<DataType[]>(['vehicles', 'reviews', 'faqs', 'pages', 'page_content'])
  const [cleanFirst, setCleanFirst] = useState(false)

  const loadMutation = useMutation({
    mutationFn: async () => {
      if (!account) throw new Error('Account is required')

      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/load-sample-data`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            account_id: account.id,
            data_types: selectedTypes,
            clean_first: cleanFirst,
          }),
        }
      )

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to load sample data')
      }

      return response.json() as Promise<LoadResponse>
    },
    onSuccess: (data) => {
      toast({
        title: 'Sample data loaded',
        description: data.message,
      })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const handleTypeChange = (type: DataType, checked: boolean) => {
    if (checked) {
      setSelectedTypes((prev) => [...prev, type])
    } else {
      setSelectedTypes((prev) => prev.filter((t) => t !== type))
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedTypes(['vehicles', 'reviews', 'faqs', 'pages', 'page_content'])
      setCleanFirst(false)
    }
    onOpenChange(open)
  }

  const dataTypeOptions = [
    {
      type: 'vehicles' as DataType,
      label: 'Vehicles',
      description: '5 vehicles with images, features, pricing & addons',
      icon: Car,
    },
    {
      type: 'reviews' as DataType,
      label: 'Reviews',
      description: '10 customer reviews with ratings',
      icon: MessageSquare,
    },
    {
      type: 'faqs' as DataType,
      label: 'FAQs',
      description: '8 sections with 32 FAQ items',
      icon: HelpCircle,
    },
    {
      type: 'pages' as DataType,
      label: 'Custom Pages',
      description: 'About, Privacy Policy, Terms, Safety pages',
      icon: FileText,
    },
    {
      type: 'page_content' as DataType,
      label: 'Page Content',
      description: 'Homepage, About, Contact, Fleet, FAQ page customizations',
      icon: LayoutTemplate,
    },
  ]

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Load Sample Data
          </DialogTitle>
          <DialogDescription>
            Load realistic sample data into <span className="font-medium">{account?.name}</span> for
            development and demo purposes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium">Data Types</Label>
            {dataTypeOptions.map((option) => (
              <div key={option.type} className="flex items-start space-x-3">
                <Checkbox
                  id={option.type}
                  checked={selectedTypes.includes(option.type)}
                  onCheckedChange={(checked) => handleTypeChange(option.type, checked === true)}
                />
                <div className="grid gap-0.5 leading-none">
                  <label
                    htmlFor={option.type}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-1.5"
                  >
                    <option.icon className="h-4 w-4 text-muted-foreground" />
                    {option.label}
                  </label>
                  <p className="text-xs text-muted-foreground">{option.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t pt-4">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="clean-first"
                checked={cleanFirst}
                onCheckedChange={(checked) => setCleanFirst(checked === true)}
              />
              <div className="grid gap-0.5 leading-none">
                <label
                  htmlFor="clean-first"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-1.5"
                >
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Clean existing data first
                </label>
                <p className="text-xs text-muted-foreground">
                  Remove existing data for selected types before loading new data
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={loadMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => loadMutation.mutate()}
            disabled={loadMutation.isPending || selectedTypes.length === 0}
          >
            {loadMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Load Sample Data
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
